import React from 'react';
import { Download } from 'lucide-react';

/**
 * Batch Processing Results Component
 */
export const BatchResults = ({
  batchJob,
  onDownload,
  onReset
}) => {
  if (!batchJob) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/20 text-green-400';
      case 'partial': return 'bg-yellow-500/20 text-yellow-400';
      case 'failed': return 'bg-red-500/20 text-red-400';
      default: return 'bg-blue-500/20 text-blue-400';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'partial': return 'Partial Success';
      case 'failed': return 'Failed';
      default: return 'Processing...';
    }
  };

  return (
    <div className="bg-bg-light p-6 rounded-lg border border-text-muted/20 mb-6" data-testid="batch-results">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Batch Processing Results</h3>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(batchJob.status)}`}>
          {getStatusText(batchJob.status)}
        </span>
      </div>
      
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-bg-dark p-3 rounded-lg text-center">
          <p className="text-2xl font-bold text-white">{batchJob.completed_files}</p>
          <p className="text-text-secondary text-sm">Completed</p>
        </div>
        <div className="bg-bg-dark p-3 rounded-lg text-center">
          <p className="text-2xl font-bold text-red-400">{batchJob.failed_files}</p>
          <p className="text-text-secondary text-sm">Failed</p>
        </div>
        <div className="bg-bg-dark p-3 rounded-lg text-center">
          <p className="text-2xl font-bold text-white">{batchJob.total_files}</p>
          <p className="text-text-secondary text-sm">Total</p>
        </div>
      </div>

      {batchJob.total_support_volume > 0 && (
        <div className="bg-bg-dark p-3 rounded-lg mb-4">
          <p className="text-text-secondary text-sm">Total Support Volume</p>
          <p className="text-xl font-bold text-white">{(batchJob.total_support_volume / 1000).toFixed(2)} cm³</p>
        </div>
      )}

      <div className="max-h-48 overflow-y-auto mb-4">
        {batchJob.files && batchJob.files.map((f, i) => (
          <div key={i} className="flex items-center justify-between py-2 border-b border-text-muted/10">
            <span className="text-white">{f.filename}</span>
            <span className={`text-sm ${
              f.status === 'completed' ? 'text-green-400' :
              f.status === 'failed' ? 'text-red-400' :
              'text-yellow-400'
            }`}>
              {f.status === 'completed' ? '✓ Done' :
               f.status === 'failed' ? `✗ ${f.error_message || 'Failed'}` :
               '⏳ Pending'}
            </span>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        {(batchJob.status === 'completed' || batchJob.status === 'partial') && (
          <button
            onClick={onDownload}
            className="flex-1 px-4 py-3 bg-accent hover:bg-accent-hover text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
            data-testid="batch-download-btn"
          >
            <Download className="w-5 h-5" />
            Download All as ZIP
          </button>
        )}
        <button
          onClick={onReset}
          className="px-4 py-3 bg-bg-dark text-text-secondary hover:text-white rounded-lg transition-colors"
          data-testid="batch-new-btn"
        >
          New Batch
        </button>
      </div>
    </div>
  );
};

export default BatchResults;
